<template>
  <WithBackdrop
    v-if="
      (!isGamestopStore && !showLegacyMetrics) ||
      data?.sys?.id !== '1sPddErj499e7I9cOhCW74'
    "
    :class="[$style.config, $style.wrap, size === 'small' && $style.small]"
    :backdrop="data?.fields?.backdrop"
    :style="data?.fields?.cssOverride"
  >
    <div :class="$style.container">
      <header :class="$style.header">
        <BaseHeading v-if="data?.fields?.header" size="h4" as="h3">
          {{ data?.fields?.header }}
        </BaseHeading>
        <BaseHeading v-if="data?.fields?.subheader" size="h5" as="h3">
          {{ data?.fields?.subheader }}
        </BaseHeading>
        <MarkedParser
          :class="['u-type-p1', $style.copy]"
          :md="data?.fields?.copy"
        />
      </header>
      <div
        v-if="data?.fields?.ctas?.filter(instanceOfCallToAction).length"
        :class="$style.ctas"
      >
        <CallToAction
          v-for="c in data?.fields?.ctas?.filter(instanceOfCallToAction)"
          :key="c.sys.id"
          :data-ctf-id="c.sys.id"
          :cta="c"
        />
      </div>
      <div
        v-if="data?.fields?.media?.fields?.file?.url"
        :class="$style.mediaImage"
      >
        <BasePicture
          :src="data?.fields?.media?.fields?.file?.url + '?q=70&w=250'"
          :alt="data?.fields?.media?.fields?.title || ''"
        />
      </div>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import type { TypeContentBlockWithoutUnresolvableLinksResponse } from '~/types/contentful'

defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
  size?: 'default' | 'small'
}>()

const route = useRoute()

const userStore = useUserStore()
const { stores } = storeToRefs(userStore)

const currentStore = computed(
  () =>
    stores.value?.find((store) => store?.id === arrayAsString(route.query.s)) ||
    stores.value?.[0],
)

const isGamestopStore = computed(() => checkIsGamestopStore(currentStore.value))

const { showLegacyMetrics } = useLegacyMetricsView()
</script>

<style lang="scss" module>
.config {
  --slimban-wrap-color: #fff;
  --slimban-mediaImage-max-width: 140px;

  @include breakpoint-variables(
    '--slimban-wrap-padding',
    (
      base: 30px 20px,
      md: 30px 20px,
    )
  );

  @include breakpoint-variables(
    '--slimban-container-gap',
    (
      base: 17px,
      md: 17px,
    )
  );

  @include breakpoint-variables(
    '--slimban-container-grid-template-columns',
    (
      base: 1fr,
      md: auto auto auto,
    )
  );

  @include breakpoint-variables(
    '--slimban-header-order',
    (
      base: 0,
      md: 0,
    )
  );

  @include breakpoint-variables(
    '--slimban-ctas-order',
    (
      base: 0,
      md: 0,
    )
  );

  @include breakpoint-variables(
    '--slimban-mediaImage-order',
    (
      base: 0,
      md: 0,
    )
  );

  &.small {
    @include breakpoint-variables(
      '--slimban-wrap-padding',
      (
        base: 10px 15px,
        md: 10px 15px,
      )
    );
  }
}

.wrap {
  padding: var(--slimban-wrap-padding);
  color: var(--slimban-wrap-color);
}

.container {
  display: grid;
  grid-template-columns: var(--slimban-container-grid-template-columns);
  gap: var(--slimban-container-gap);
  align-items: center;
  text-align: center;

  min-height: 90px;
  max-width: 1350px;
  margin: 0 auto;

  @include media('md') {
    text-align: left;
  }
}

.header {
  order: var(--slimban-header-order);
  display: grid;
  gap: 4px;
}

.content {
  display: grid;
  gap: 18px;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: var(--slimban-roboto-condensed);
  }

  h1,
  h2 {
    @include fluid-type(26px, 42px);
  }
}

.copy {
  @include fluid-type(16px, 19px);

  .small & {
    font-size: 16px;
  }
}

.ctas {
  order: var(--slimban-ctas-order);
  margin: 0 auto;

  @include media('md') {
    margin: auto;
  }
}

.mediaImage {
  order: var(--slimban-mediaImage-order);
  max-width: var(--slimban-mediaImage-max-width);
  margin: 0 auto;

  @include media('md') {
    margin: auto;
  }
}
.img {
  max-width: 100%;
  object-fit: contain;
}
</style>
